import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS, TEventResultsList } from '@root/src/types';
import { LazyLoader } from '@root/src/hooks/useLazyLoading';
import routeList, { QUERY_ENUM } from '@services/routeList';
import { selectEventEngagedCantonsOptions, selectEventEngagedNationalitiesOptions, selectEventSlug } from '@features/eventDetail/slices/eventDetailServerSlice';
import EventListPageTemplate from '@features/eventDetail/templates/EventListPageTemplate';
import {
  selectLiveResultsOptionsForCurrentResultsListType,
  selectLiveResultsResultsTypesAvailable,
  selectResultsList,
  selectResultsListFilter,
  selectResultsListType,
  setResultsListFilter,
} from '@features/results/slices/resultsSlice';
import EventRankingList from '@features/results/components/EventRankingList';
import useLiveResultsLazyLoading from '@features/results/useLiveResultsLazyLoading';
import { getQueryEventId } from '@features/eventDetail/utils';
import { SEARCH_TYPES } from '@features/ui/components/SearchInput';

const EventLiveResultsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const eventId = getQueryEventId(router.query);
  const eventSlug = useSelector(selectEventSlug(eventId));
  const queryTypeId = router.query[QUERY_ENUM.typeId]
    && parseInt(router.query[QUERY_ENUM.typeId] as string, 10);

  const resultsListType = useSelector(selectResultsListType);
  const resultsListOptions = useSelector(selectLiveResultsOptionsForCurrentResultsListType);
  const resultsList = useSelector(selectResultsList) as TEventResultsList;

  const engagedCantonsOptions = useSelector(selectEventEngagedCantonsOptions(eventId));
  const engagedNationalitiesOptions = useSelector(selectEventEngagedNationalitiesOptions(eventId));
  const resultsFilter = useSelector(selectResultsListFilter);
  const searchOptions = getSearchOptions(t, engagedCantonsOptions, engagedNationalitiesOptions);

  const resultsListAvailableRankingTypes = useSelector(selectLiveResultsResultsTypesAvailable);
  const tagList = resultsListAvailableRankingTypes
    .map(key => ({
      label: t(`eventResultsList_tag${key}.message`),
      value: key,
    }));

  const onFiltersChange = (query) => router.push(routeList.events(eventSlug)
    .liveResults(query), null, { scroll: false });

  const { observerLoadMoreRef, isLoading, hasMore } = useLiveResultsLazyLoading({
    eventId,
    queryTypeId,
  });

  return (
    <EventListPageTemplate
      pageTitle={t('eventResultsList_pageHeaderTitle.message')}
      groupOptions={resultsListOptions}
      groupOptionsValue={queryTypeId || resultsListOptions[0].value}
      onGroupChange={(group: number) => onFiltersChange({
        type: resultsListType,
        typeId: group > -1 ? group : null,
      })}
      tagList={tagList}
      tagActiveValue={resultsListType}
      onTagChange={(tagValue) => onFiltersChange({ type: tagValue })}
      searchOptions={searchOptions}
      onSearchSubmit={(query, filterBy) => dispatch(setResultsListFilter({
        query,
        filterBy,
      }))}
      withResultSubscriptions
      filter={resultsFilter}
    >
      {resultsList.map(rankingList => (
        <EventRankingList rankingId={rankingList._idMso} key={rankingList._idMso} />
      ))}

      <LazyLoader isLoading={isLoading} hasMore={hasMore} ref={observerLoadMoreRef} />
    </EventListPageTemplate>
  );
};

export default EventLiveResultsList;

export const getSearchOptions = (t, engagedCantonsOptions, engagedNationalitiesOptions) => [
  {
    label: t('eventList_searchOptionAthlete.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.athlete,
    type: SEARCH_TYPES.TEXT,
  },
  {
    label: t('eventList_searchOptionState.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.canton,
    type: SEARCH_TYPES.SELECT,
    options: engagedCantonsOptions,
  },
  {
    label: t('eventList_searchOptionYear.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.year,
    type: SEARCH_TYPES.BIRTH_YEAR,
  },
  {
    label: t('eventList_searchOptionLocality.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.location,
    type: SEARCH_TYPES.TEXT,
  },
  {
    label: t('eventList_searchOptionNationality.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.nationality,
    type: SEARCH_TYPES.SELECT,
    options: engagedNationalitiesOptions,
  },
  {
    label: t('eventList_searchOptionTeam.message'),
    value: LIVE_RESULTS_LIST_SEARCH_FILTER_KEYS.teamname,
    type: SEARCH_TYPES.TEXT,
  },
];
